import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";
import { SectionLabel } from "@/components/ui-bits";
import { Reveal } from "@/components/Reveal";

function PriceDisplay({ value, period }) {
  const raw = String(value || "").trim();
  const startsFrom = /^mulai\s+/i.test(raw);
  const clean = raw.replace(/^mulai\s+/i, "");
  const rangeParts = clean.split(/\s*[–—]\s*/).filter(Boolean);
  const isRange = rangeParts.length === 2;

  return (
    <div className="pricing-price-block mb-4 mt-2.5 flex min-h-[88px] flex-col justify-center sm:mb-6 sm:mt-4 sm:min-h-[104px]">
      <span className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary sm:text-xs">
        {startsFrom ? "Mulai dari" : isRange ? "Rentang harga" : "Harga paket"}
      </span>
      {isRange ? (
        <span className="font-display text-[1.65rem] font-extrabold leading-[1.04] tracking-tight sm:text-[1.9rem] md:text-[2.05rem]">
          <span className="block">{rangeParts[0]}</span>
          <span className="mt-1 block text-[0.72em] text-muted-foreground">– {rangeParts[1]}</span>
        </span>
      ) : (
        <span className="break-words font-display text-[1.75rem] font-extrabold leading-[1.04] tracking-tight sm:text-[2rem] md:text-[2.2rem]">{clean}</span>
      )}
      {period && <span className="mt-1 text-xs text-muted-foreground sm:text-sm">{period}</span>}
    </div>
  );
}

export default function PricingTabs({ fullDetails = false, heading = true, homeSection }) {
  const { settings } = useSite();
  const trackRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });
  const { data: tabs = [] } = useQuery({ queryKey: ["pricing"], queryFn: async () => (await api.get("/pricing")).data });
  const [active, setActive] = useState(0);
  if (tabs.length === 0) return null;
  const wa = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  const current = tabs[active] || tabs[0];
  const packages = current.packages || [];

  const scrollPackages = (direction) => {
    trackRef.current?.scrollBy({ left: direction * 256, behavior: "smooth" });
  };

  const startDrag = (event) => {
    if (event.pointerType !== "mouse" || !trackRef.current) return;
    dragRef.current = { active: true, startX: event.clientX, scrollLeft: trackRef.current.scrollLeft, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!dragRef.current.active || !trackRef.current) return;
    const distance = event.clientX - dragRef.current.startX;
    if (Math.abs(distance) > 4) dragRef.current.moved = true;
    trackRef.current.scrollLeft = dragRef.current.scrollLeft - distance;
  };

  const endDrag = (event) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const waLink = (tab) => {
    const msg = encodeURIComponent(tab.wa_message || `Halo Digitalis, saya ingin konsultasi paket ${tab.name}.`);
    return wa ? `https://wa.me/${wa}?text=${msg}` : "/contact";
  };

  return (
    <section className="py-16 md:py-32 bg-surface" data-testid="pricing-section" id="harga">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        {heading && <div className="text-center flex flex-col items-center mb-12">
          <SectionLabel>Harga & Paket</SectionLabel>
          <h2 className="font-display font-extrabold tracking-tightest leading-tight text-4xl md:text-5xl lg:text-6xl">{homeSection?.title || "Layanan Kami"}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mt-4">{homeSection?.subtitle || "Kembangkan bisnis melalui strategi, konten, website, influencer, dan optimasi digital yang disesuaikan dengan kebutuhan brand."}</p>
        </div>}

        {/* Tabs */}
        <div className="mb-8 grid grid-cols-2 gap-2 sm:mb-10 sm:flex sm:flex-wrap sm:justify-center" data-testid="pricing-tablist">
          {tabs.map((t, i) => (
            <button
              key={t.id || t.slug}
              onClick={() => { setActive(i); trackRef.current?.scrollTo({ left: 0, behavior: "smooth" }); }}
              data-testid={`pricing-tab-${t.slug}`}
              className={`min-h-10 w-full rounded-2xl px-3 py-2 text-[11px] font-semibold leading-tight transition-all sm:min-h-11 sm:w-auto sm:rounded-full sm:px-5 sm:py-2.5 sm:text-sm ${active === i ? "btn-primary soft-shadow" : "btn-grid-outline text-muted-foreground hover:text-foreground"}`}
            >
              {t.name}
            </button>
          ))}
        </div>

        <p className="text-center text-muted-foreground mb-10">{current.subtitle}</p>

        {/* Packages */}
        <div
          ref={trackRef}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={(event) => { if (dragRef.current.moved) { event.preventDefault(); event.stopPropagation(); dragRef.current.moved = false; } }}
          className="pricing-track -mx-5 grid cursor-grab auto-cols-[244px] grid-flow-col items-stretch gap-3 overflow-x-auto overflow-y-hidden px-5 pb-4 pt-3 snap-x snap-mandatory overscroll-x-contain touch-auto select-none active:cursor-grabbing sm:mx-auto sm:max-w-5xl sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0 sm:pt-3 md:gap-6 lg:grid-cols-3"
        >
          {packages.map((p, i) => (
            <Reveal key={i} delay={i * 0.06} y={0} className="min-w-0 snap-start">
              <div className={`relative flex h-full min-w-0 flex-col rounded-2xl border p-5 transition-all sm:rounded-3xl md:p-6 lg:p-8 ${p.highlighted ? "border-primary bg-card soft-shadow lg:scale-[1.02]" : "border-border bg-card/60 hover:border-primary/40"}`} data-testid={`pricing-package-${current.slug}-${i}`}>
                {p.highlighted && <span className="absolute -top-3 left-5 z-10 rounded-full bg-primary px-3 py-1 text-[10px] font-bold text-primary-foreground sm:left-1/2 sm:-translate-x-1/2 md:px-4 md:text-xs">Populer</span>}
                <p className="text-base font-semibold sm:text-lg">{p.title}</p>
                <PriceDisplay value={p.price} period={p.period} />
                {p.description && <p className="mb-4 text-xs leading-relaxed text-muted-foreground sm:min-h-[44px] sm:text-sm">{p.description}</p>}
                <ul className="space-y-2 sm:space-y-3">
                  {(fullDetails ? (p.features || []) : (p.features || []).slice(0, 7)).map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs leading-relaxed sm:gap-2.5 sm:text-sm">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:h-5 sm:w-5"><Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" strokeWidth={3} /></span>
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                {!fullDetails && (p.features || []).length > 7 && (
                  <details className="pricing-more mb-4 mt-2 text-xs sm:text-sm">
                    <summary className="min-h-9 cursor-pointer py-2 font-semibold text-primary">+ {(p.features || []).length - 7} manfaat lainnya</summary>
                    <ul className="space-y-2 pt-2 sm:space-y-3">
                      {(p.features || []).slice(7).map((f, j) => <li key={j} className="flex items-start gap-2 leading-relaxed"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span className="text-muted-foreground">{f}</span></li>)}
                    </ul>
                  </details>
                )}
                {p.goal && <div className="mb-5 mt-4 rounded-xl border border-primary/20 bg-primary/5 p-3 text-[11px] leading-relaxed text-muted-foreground sm:text-xs"><strong className="text-foreground">Tujuan paket:</strong> {p.goal}</div>}
                {!p.goal && <div className="flex-1" />}
                <a
                  href={waLink(current)}
                  target={wa ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  onClick={() => { if (window.gtag) window.gtag("event", "pricing_whatsapp_click", { tab: current.name }); }}
                  data-testid={`pricing-cta-${current.slug}-${i}`}
                  className={`pricing-cta inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all hover:-translate-y-0.5 sm:px-5 sm:py-2.5 sm:text-sm md:py-3.5 ${p.highlighted ? "btn-primary hover:opacity-90" : "btn-grid-outline"}`}
                >
                  <MessageCircle className="h-4 w-4 shrink-0" /> {p.cta_text || "Pesan Sekarang"}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        {packages.length > 1 && (
          <div className="mt-2 flex items-center justify-between gap-3 sm:hidden">
            <p className="text-xs text-muted-foreground">Geser atau gunakan tombol untuk melihat paket lainnya.</p>
            <div className="flex shrink-0 gap-2">
              <button type="button" onClick={() => scrollPackages(-1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card" aria-label="Paket sebelumnya"><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" onClick={() => scrollPackages(1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-primary bg-card text-primary" aria-label="Paket berikutnya"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
        {(current.terms || []).length > 0 && (
          <details open={fullDetails || undefined} className={`${fullDetails ? "" : "hidden sm:block "}mx-auto mt-8 max-w-5xl rounded-2xl border border-border bg-card/60 p-5 sm:p-6`}>
            <summary className="cursor-pointer font-display text-sm font-bold sm:text-base">Ketentuan {current.name}</summary>
            <ul className="mt-4 grid gap-2 text-xs leading-relaxed text-muted-foreground sm:grid-cols-2 sm:text-sm">
              {current.terms.map((term, index) => <li key={index} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{term}</li>)}
            </ul>
          </details>
        )}
        <p className={`${fullDetails ? "" : "hidden sm:block "}mt-8 text-center text-xs text-muted-foreground`}>Harga bersifat default dan dapat berubah. Konsultasikan kebutuhan spesifik Anda dengan tim Digitalis.</p>
        {!fullDetails && <div className="mt-5 hidden justify-center sm:flex"><Link to="/pricing" className="btn-grid-outline inline-flex min-h-11 items-center justify-center rounded-full px-6 text-sm font-semibold">Lihat semua harga & manfaat →</Link></div>}
      </div>
    </section>
  );
}
